// firebase/csvImport.service.js
// CSV Lead Import Service - Handles parsing, validation, deduplication, and batch import
import Papa from 'papaparse';
import {
    collection,
    addDoc,
    getDocs,
    query,
    where,
    writeBatch,
    serverTimestamp,
    doc,
} from 'firebase/firestore';
import { db } from './firebase.config';
import { LEAD_STATUS, VALID_STATUSES, generateFullName, findLeadByEmail } from './leads.service';

// ============================================================
// CONSTANTS
// ============================================================

/**
 * Maximum file size in bytes (10MB)
 */
export const MAX_FILE_SIZE = 10 * 1024 * 1024;

/**
 * Maximum operations per Firestore batch (using 400 for safety margin)
 */
const BATCH_SIZE = 400;

/**
 * Delay between batch commits to avoid rate limiting (ms)
 */
const BATCH_DELAY_MS = 50;

/**
 * CSV column name to lead field mapping
 * Supports common variations of column names
 */
const COLUMN_ALIASES = {
    firstName: ['first name', 'firstname', 'first_name', 'fname'],
    lastName: ['last name', 'lastname', 'last_name', 'lname'],
    email: ['email', 'email address', 'emailaddress', 'e-mail'],
    phone: ['phone', 'phone number', 'phonenumber', 'mobile', 'cell', 'telephone'],
    address: ['address', 'street address', 'location'],
    tags: ['tags', 'labels', 'categories'],
    status: ['status', 'lead status', 'state'],
};

// ============================================================
// CSV PARSING
// ============================================================

/**
 * Parse a CSV file and return structured data
 * 
 * @param {File} file - The CSV file to parse
 * @returns {Promise<{ success: boolean, data?: Array, headers?: string[], error?: string }>}
 */
export const parseCSV = (file) => {
    return new Promise((resolve) => {
        if (!file) {
            resolve({ success: false, error: 'No file provided' });
            return;
        }

        if (file.size > MAX_FILE_SIZE) {
            resolve({ success: false, error: `File too large. Maximum size is ${MAX_FILE_SIZE / 1024 / 1024}MB` });
            return;
        }

        Papa.parse(file, {
            header: true,
            skipEmptyLines: true,
            transformHeader: (header) => header.trim().toLowerCase(),
            complete: (results) => {
                if (results.errors.length > 0) {
                    const errorMessages = results.errors.map(e => e.message).join(', ');
                    resolve({ success: false, error: `CSV parsing error: ${errorMessages}` });
                    return;
                }

                if (results.data.length === 0) {
                    resolve({ success: false, error: 'CSV file is empty' });
                    return;
                }

                resolve({
                    success: true,
                    data: results.data,
                    headers: results.meta.fields,
                });
            },
            error: (error) => {
                resolve({ success: false, error: error.message });
            },
        });
    });
};

/**
 * Map CSV row to lead field structure
 * Handles various column name variations
 * 
 * @param {Object} row - Raw CSV row with lowercase headers
 * @returns {Object} - Normalized lead data
 */
export const mapRowToLead = (row) => {
    const findValue = (aliases) => {
        for (const alias of aliases) {
            if (row[alias] !== undefined && row[alias] !== null && row[alias] !== '') {
                return row[alias].trim();
            }
        }
        return '';
    };

    const firstName = findValue(COLUMN_ALIASES.firstName);
    const lastName = findValue(COLUMN_ALIASES.lastName);
    const email = findValue(COLUMN_ALIASES.email).toLowerCase();
    const phone = findValue(COLUMN_ALIASES.phone);
    const address = findValue(COLUMN_ALIASES.address);
    const tagsRaw = findValue(COLUMN_ALIASES.tags);
    const statusRaw = findValue(COLUMN_ALIASES.status);

    // Parse tags from comma-separated string
    const tags = tagsRaw
        ? tagsRaw.split(',').map(t => t.trim()).filter(Boolean)
        : [];

    // Validate and normalize status
    const normalizedStatus = statusRaw
        ? VALID_STATUSES.find(s => s.toLowerCase() === statusRaw.toLowerCase())
        : null;
    const status = normalizedStatus || LEAD_STATUS.ACTIVE;

    return {
        firstName,
        lastName,
        fullName: generateFullName(firstName, lastName),
        email,
        phone,
        address,
        tags,
        status,
    };
};

// ============================================================
// VALIDATION
// ============================================================

/**
 * Validate email format
 * @param {string} email 
 * @returns {boolean}
 */
const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
};

/**
 * Validate a single lead row
 * 
 * @param {Object} lead - Mapped lead data
 * @param {number} rowIndex - Original row index for error reporting
 * @returns {{ valid: boolean, errors: string[], warnings: string[] }}
 */
export const validateLeadRow = (lead, rowIndex) => {
    const errors = [];
    const warnings = [];
    const rowNum = rowIndex + 2; // +2 for 1-indexing and header row

    // Required: Email
    if (!lead.email) {
        errors.push(`Row ${rowNum}: Email is required`);
    } else if (!isValidEmail(lead.email)) {
        errors.push(`Row ${rowNum}: Invalid email format "${lead.email}"`);
    }

    // Recommended: First name
    if (!lead.firstName) {
        warnings.push(`Row ${rowNum}: First name is empty`);
    }

    // Recommended: Last name
    if (!lead.lastName) {
        warnings.push(`Row ${rowNum}: Last name is empty`);
    }

    return {
        valid: errors.length === 0,
        errors,
        warnings,
    };
};

/**
 * Validate all rows and return categorized results
 * 
 * @param {Array<Object>} rows - Raw CSV rows
 * @returns {{ validLeads: Array, invalidRows: Array, allWarnings: string[] }}
 */
export const validateAllRows = (rows) => {
    const validLeads = [];
    const invalidRows = [];
    const allWarnings = [];
    const seenEmails = new Set();

    rows.forEach((row, index) => {
        const lead = mapRowToLead(row);
        const validation = validateLeadRow(lead, index);

        // Check for duplicates within CSV
        if (lead.email && seenEmails.has(lead.email)) {
            validation.errors.push(`Row ${index + 2}: Duplicate email "${lead.email}" in CSV`);
            validation.valid = false;
        }

        if (validation.valid) {
            seenEmails.add(lead.email);
            validLeads.push({ ...lead, rowIndex: index });
        } else {
            invalidRows.push({
                rowIndex: index,
                rowNum: index + 2,
                data: lead,
                errors: validation.errors,
            });
        }

        allWarnings.push(...validation.warnings);
    });

    return { validLeads, invalidRows, allWarnings };
};

// ============================================================
// DEDUPLICATION
// ============================================================

/**
 * Check which leads already exist in Firestore (by email)
 * 
 * @param {string} userId - Owner's user ID
 * @param {Array<Object>} leads - Leads to check
 * @returns {Promise<{ toCreate: Array, toUpdate: Array }>}
 */
export const checkExistingLeads = async (userId, leads) => {
    const toCreate = [];
    const toUpdate = [];

    // Fetch all leads for efficiency (if not too many)
    const leadsRef = collection(db, 'leads');
    const q = query(leadsRef, where('userId', '==', userId));
    const snapshot = await getDocs(q);

    // Create email -> lead mapping for quick lookup
    const existingByEmail = new Map();
    snapshot.docs.forEach(docSnap => {
        const data = docSnap.data();
        if (data.email) {
            existingByEmail.set(data.email.toLowerCase(), {
                id: docSnap.id,
                ...data,
            });
        }
    });

    // Categorize leads
    for (const lead of leads) {
        const existing = existingByEmail.get(lead.email);
        if (existing) {
            toUpdate.push({ ...lead, existingId: existing.id, existingData: existing });
        } else {
            toCreate.push(lead);
        }
    }

    return { toCreate, toUpdate };
};

// ============================================================
// BATCH IMPORT
// ============================================================

/**
 * Sleep utility for batch delays
 */
const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Bulk import leads into Firestore using batched writes
 * 
 * @param {string} userId - Owner's user ID
 * @param {Array<Object>} leadsToCreate - New leads to create
 * @param {Array<Object>} leadsToUpdate - Existing leads to update
 * @param {Function} onProgress - Progress callback ({ current, total, phase })
 * @returns {Promise<{ created: number, updated: number, failed: number, errors: Array }>}
 */
export const bulkImportLeads = async (userId, leadsToCreate, leadsToUpdate, onProgress = null) => {
    const results = {
        created: 0,
        updated: 0,
        failed: 0,
        errors: [],
    };

    const totalOperations = leadsToCreate.length + leadsToUpdate.length;
    let completedOperations = 0;

    // Helper to report progress
    const reportProgress = (phase) => {
        if (onProgress) {
            onProgress({
                current: completedOperations,
                total: totalOperations,
                phase,
            });
        }
    };

    reportProgress('starting');

    // ---- PHASE 1: CREATE NEW LEADS ----
    const createChunks = [];
    for (let i = 0; i < leadsToCreate.length; i += BATCH_SIZE) {
        createChunks.push(leadsToCreate.slice(i, i + BATCH_SIZE));
    }

    for (const chunk of createChunks) {
        const batch = writeBatch(db);

        for (const lead of chunk) {
            try {
                const newDocRef = doc(collection(db, 'leads'));
                batch.set(newDocRef, {
                    userId,
                    name: lead.fullName,
                    fullName: lead.fullName,
                    firstName: lead.firstName,
                    lastName: lead.lastName,
                    email: lead.email,
                    phone: lead.phone || '',
                    address: lead.address || '',
                    tags: lead.tags || [],
                    status: lead.status || LEAD_STATUS.ACTIVE,
                    notes: '',
                    interestedProperties: [],
                    createdAt: serverTimestamp(),
                    updatedAt: serverTimestamp(),
                    importedAt: serverTimestamp(),
                    importSource: 'csv',
                });
            } catch (err) {
                results.failed++;
                results.errors.push(`Create failed for "${lead.email}": ${err.message}`);
            }
        }

        try {
            await batch.commit();
            results.created += chunk.length;
            completedOperations += chunk.length;
            reportProgress('creating');
        } catch (err) {
            results.failed += chunk.length;
            results.errors.push(`Batch create failed: ${err.message}`);
        }

        // Small delay between batches
        if (createChunks.indexOf(chunk) < createChunks.length - 1) {
            await sleep(BATCH_DELAY_MS);
        }
    }

    // ---- PHASE 2: UPDATE EXISTING LEADS ----
    const updateChunks = [];
    for (let i = 0; i < leadsToUpdate.length; i += BATCH_SIZE) {
        updateChunks.push(leadsToUpdate.slice(i, i + BATCH_SIZE));
    }

    for (const chunk of updateChunks) {
        const batch = writeBatch(db);

        for (const lead of chunk) {
            try {
                const leadRef = doc(db, 'leads', lead.existingId);
                batch.update(leadRef, {
                    // Update contact info (preserve existing if new is empty)
                    firstName: lead.firstName || lead.existingData.firstName || '',
                    lastName: lead.lastName || lead.existingData.lastName || '',
                    fullName: lead.fullName || lead.existingData.fullName || '',
                    name: lead.fullName || lead.existingData.name || '',
                    phone: lead.phone || lead.existingData.phone || '',
                    address: lead.address || lead.existingData.address || '',
                    // Merge tags (keep existing + add new)
                    tags: [...new Set([...(lead.existingData.tags || []), ...lead.tags])],
                    // Preserve status unless it's a valid new status
                    // Don't overwrite status, notes, conversations
                    updatedAt: serverTimestamp(),
                    lastCsvImportAt: serverTimestamp(),
                });
            } catch (err) {
                results.failed++;
                results.errors.push(`Update failed for "${lead.email}": ${err.message}`);
            }
        }

        try {
            await batch.commit();
            results.updated += chunk.length;
            completedOperations += chunk.length;
            reportProgress('updating');
        } catch (err) {
            results.failed += chunk.length;
            results.errors.push(`Batch update failed: ${err.message}`);
        }

        // Small delay between batches
        if (updateChunks.indexOf(chunk) < updateChunks.length - 1) {
            await sleep(BATCH_DELAY_MS);
        }
    }

    reportProgress('complete');

    console.log('[csvImport.service] Import complete:', results);
    return results;
};

// ============================================================
// MAIN IMPORT FUNCTION
// ============================================================

/**
 * Complete CSV import pipeline
 * Parses, validates, checks duplicates, and imports leads
 * 
 * @param {string} userId - Owner's user ID
 * @param {File} file - CSV file to import
 * @param {Function} onProgress - Progress callback
 * @returns {Promise<Object>} - Import results
 */
export const importLeadsFromCSV = async (userId, file, onProgress = null) => {
    if (!userId) throw new Error('userId is required');
    if (!file) throw new Error('file is required');

    // Step 1: Parse CSV
    const parseResult = await parseCSV(file);
    if (!parseResult.success) {
        return {
            success: false,
            phase: 'parse',
            error: parseResult.error,
        };
    }

    // Step 2: Validate all rows
    const { validLeads, invalidRows, allWarnings } = validateAllRows(parseResult.data);

    if (validLeads.length === 0) {
        return {
            success: false,
            phase: 'validate',
            error: 'No valid leads found in CSV',
            invalidRows,
            warnings: allWarnings,
        };
    }

    // Step 3: Check for existing leads (deduplication)
    const { toCreate, toUpdate } = await checkExistingLeads(userId, validLeads);

    // Step 4: Batch import
    const importResults = await bulkImportLeads(userId, toCreate, toUpdate, onProgress);

    return {
        success: true,
        phase: 'complete',
        totalRows: parseResult.data.length,
        validRows: validLeads.length,
        invalidRows,
        warnings: allWarnings,
        created: importResults.created,
        updated: importResults.updated,
        failed: importResults.failed,
        errors: importResults.errors,
    };
};

export default {
    parseCSV,
    validateAllRows,
    checkExistingLeads,
    bulkImportLeads,
    importLeadsFromCSV,
    MAX_FILE_SIZE,
};
